import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Radio, FormControlLabel, FormControl, FormLabel, RadioGroup, Checkbox, FormHelperText } from '@mui/material';
import { CustomDatePicker } from './CustomDatePicker';
import { ValidateUtils } from '@shared/utils';
import { Input } from '@shared/widgets';

export const StepPEP = () => {
  const [isPep, setIsPep] = useState<boolean>(false);

  const { control, formState, watch, setValue, setError, clearErrors } = useFormContext();
  const pep = watch('pep');
  const formerPep = watch('formerPep');
  const familyPep = watch('familyPep');
  const closeAssociatePep = watch('closeAssociatePep');

  useEffect(() => {
    setIsPep((prev) => prev || [pep, formerPep, familyPep, closeAssociatePep].some(Boolean));
  }, [pep, formerPep, familyPep, closeAssociatePep]);

  useEffect(() => {
    if (!isPep) return;
    if ([pep, formerPep, familyPep, closeAssociatePep].some(Boolean)) {
      clearErrors('pepGroup');
      return;
    }
    setError('pepGroup', { message: 'At least one option must be selected' });
  }, [formState.submitCount, isPep, pep, formerPep, familyPep, closeAssociatePep]);

  const reset = () => {
    setValue('pep', false);
    setValue('pepRolePosition', '');
    setValue('currentPepDate', null);
    setValue('formerPep', false);
    setValue('formerPepPosition', '');
    setValue('formerPepDate', null);
    setValue('familyPep', false);
    setValue('familyPepRelationship', '');
    setValue('familyPepPosition', '');
    setValue('familyPepDate', null);
    setValue('closeAssociatePep', false);
    setValue('closeAssociatePepRelationship', '');
    setValue('closeAssociatePepPosition', '');
    setValue('closeAssociatePepDate', null);
    clearErrors('pepGroup');
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormControl margin='normal' fullWidth>
          <FormLabel component='label' style={{ marginBottom: '8px' }}>
            Have you, or an immediate family member, or a close associate been entrusted, whether presently or in the past, with a prominent public
            function? *
          </FormLabel>
          <FormLabel>
            If yes, please specify role/position* (Insignia Cards Ltd. may request additional information as required under the relevant legislation)
            *
          </FormLabel>
          <RadioGroup row>
            <FormControlLabel
              label='Yes, Further details are required'
              labelPlacement='end'
              control={<Radio color='primary' value={true} checked={isPep} onChange={() => setIsPep(true)} />}
            />
            <FormControlLabel
              label='No'
              labelPlacement='end'
              control={
                <Radio
                  color='primary'
                  value={false}
                  checked={!isPep}
                  onChange={() => {
                    setIsPep(false);
                    reset();
                  }}
                />
              }
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {isPep && (
        <>
          <Grid item xs={12}>
            <FormControlLabel
              label='I declare I am currently a PEP'
              control={
                <Controller
                  name='pep'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('pepRolePosition', '');
                        setValue('currentPepDate', null);
                      }}
                    />
                  )}
                />
              }
            />
          </Grid>
          {pep && (
            <>
              <Grid item xs={6}>
                <Controller
                  name='pepRolePosition'
                  control={control}
                  rules={{ required: 'PEP position is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='PEP position' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name='currentPepDate'
                  control={control}
                  rules={{
                    required: 'Date of appointment is required',
                    validate: { isValidDate: ValidateUtils.isValidDate },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      {...field}
                      label='Date of appointment'
                      margin='normal'
                      inputFormat='dd/MM/yyyy'
                      fullWidth
                      required
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              label='I declare I am a former PEP'
              control={
                <Controller
                  name='formerPep'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('formerPepPosition', '');
                        setValue('formerPepDate', null);
                      }}
                    />
                  )}
                />
              }
            />
          </Grid>
          {formerPep && (
            <>
              <Grid item xs={6}>
                <Controller
                  name='formerPepPosition'
                  control={control}
                  rules={{ required: 'PEP position is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='PEP position' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name='formerPepDate'
                  control={control}
                  rules={{
                    required: 'Date of termination is required',
                    validate: { isValidDate: ValidateUtils.isValidDate },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      {...field}
                      label='Date of termination'
                      margin='normal'
                      inputFormat='dd/MM/yyyy'
                      fullWidth
                      required
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              label='I declare that I am an immediate family member of a PEP'
              control={
                <Controller
                  name='familyPep'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('familyPepRelationship', '');
                        setValue('familyPepPosition', '');
                        setValue('familyPepDate', null);
                      }}
                    />
                  )}
                />
              }
            />
          </Grid>
          {familyPep && (
            <>
              <Grid item xs={4}>
                <Controller
                  name='familyPepRelationship'
                  control={control}
                  rules={{ required: 'Relationship is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='Relationship' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name='familyPepPosition'
                  control={control}
                  rules={{ required: 'Position of PEP is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='Position of PEP' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name='familyPepDate'
                  control={control}
                  rules={{
                    required: 'Date of appointment is required',
                    validate: { isValidDate: ValidateUtils.isValidDate },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      {...field}
                      label='Date of appointment'
                      margin='normal'
                      inputFormat='dd/MM/yyyy'
                      fullWidth
                      required
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              label='I declare that I am close associate to a PEP'
              control={
                <Controller
                  name='closeAssociatePep'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('closeAssociatePepRelationship', '');
                        setValue('closeAssociatePepPosition', '');
                        setValue('closeAssociatePepDate', null);
                      }}
                    />
                  )}
                />
              }
            />
            {formState.errors.pepGroup?.message && <FormHelperText error>At least one option must be selected</FormHelperText>}
          </Grid>
          {closeAssociatePep && (
            <>
              <Grid item xs={4}>
                <Controller
                  name='closeAssociatePepRelationship'
                  control={control}
                  rules={{ required: 'Relationship is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='Relationship' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name='closeAssociatePepPosition'
                  control={control}
                  rules={{ required: 'Position of PEP is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Input {...field} label='Position of PEP' margin='normal' required error={!!error} helperText={error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name='closeAssociatePepDate'
                  control={control}
                  rules={{
                    required: 'Date of appointment is required',
                    validate: { isValidDate: ValidateUtils.isValidDate },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomDatePicker
                      {...field}
                      label='Date of appointment'
                      margin='normal'
                      inputFormat='dd/MM/yyyy'
                      fullWidth
                      required
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};
